import { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { Modal } from '../../design-system';
import LoadingSpinner from '../common/LoadingSpinner';

const BedDispositionDialog = ({ disposition, onSave, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: disposition?.name || '',
    min_beds: disposition?.min_beds || 1,
    max_beds: disposition?.max_beds || '',
    sheet_cost: disposition?.sheet_cost || 0,
    towel_cost: disposition?.towel_cost || 0
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!disposition;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (!formData.min_beds || formData.min_beds < 1) {
      newErrors.min_beds = 'El mínimo de camas debe ser al menos 1';
    }

    if (formData.max_beds && parseInt(formData.max_beds) < parseInt(formData.min_beds)) {
      newErrors.max_beds = 'El máximo debe ser mayor o igual al mínimo';
    }

    if (formData.sheet_cost < 0) {
      newErrors.sheet_cost = 'El coste no puede ser negativo';
    }

    if (formData.towel_cost < 0) {
      newErrors.towel_cost = 'El coste no puede ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = {
        name: formData.name,
        min_beds: parseInt(formData.min_beds),
        sheet_cost: parseFloat(formData.sheet_cost) || 0,
        towel_cost: parseFloat(formData.towel_cost) || 0
      };

      if (formData.max_beds !== '' && formData.max_beds !== null) {
        dataToSend.max_beds = parseInt(formData.max_beds);
      } else {
        dataToSend.max_beds = null;
      }

      await onSave(dataToSend);
    } catch (error) {
      console.error('Error saving bed disposition:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="md"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Editar Disposición de Camas' : 'Nueva Disposición de Camas'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4" id="bed-disposition-form">
          {/* Name */}
          <div>
            <label htmlFor="name" className="form-label">
              Nombre *
            </label>
            <input
              id="name"
              type="text"
              className={`form-input ${errors.name ? 'border-danger-500' : ''}`}
              placeholder="Ej: Individual, Matrimonio, Separadas"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-danger-600">{errors.name}</p>
            )}
          </div>

          {/* Bed count range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="min_beds" className="form-label">
                Número mínimo de camas *
              </label>
              <input
                id="min_beds"
                type="number"
                min="1"
                max="10"
                className={`form-input ${errors.min_beds ? 'border-danger-500' : ''}`}
                value={formData.min_beds}
                onChange={(e) => handleChange('min_beds', e.target.value)}
              />
              {errors.min_beds && (
                <p className="mt-1 text-sm text-danger-600">{errors.min_beds}</p>
              )}
            </div>

            <div>
              <label htmlFor="max_beds" className="form-label">
                Número máximo de camas
              </label>
              <input
                id="max_beds"
                type="number"
                min={formData.min_beds || 1}
                max="10"
                className={`form-input ${errors.max_beds ? 'border-danger-500' : ''}`}
                placeholder="Opcional"
                value={formData.max_beds}
                onChange={(e) => handleChange('max_beds', e.target.value)}
              />
              {errors.max_beds && (
                <p className="mt-1 text-sm text-danger-600">{errors.max_beds}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Dejar vacío para sin límite
              </p>
            </div>
          </div>

          {/* Laundry costs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sheet_cost" className="form-label">
                Coste de sábanas (€)
              </label>
              <input
                id="sheet_cost"
                type="number"
                min="0"
                step="0.01"
                className={`form-input ${errors.sheet_cost ? 'border-danger-500' : ''}`}
                placeholder="0.00"
                value={formData.sheet_cost}
                onChange={(e) => handleChange('sheet_cost', e.target.value)}
              />
              {errors.sheet_cost && (
                <p className="mt-1 text-sm text-danger-600">{errors.sheet_cost}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Coste estimado de lavandería de sábanas
              </p>
            </div>

            <div>
              <label htmlFor="towel_cost" className="form-label">
                Coste de toallas (€)
              </label>
              <input
                id="towel_cost"
                type="number"
                min="0"
                step="0.01"
                className={`form-input ${errors.towel_cost ? 'border-danger-500' : ''}`}
                placeholder="0.00"
                value={formData.towel_cost}
                onChange={(e) => handleChange('towel_cost', e.target.value)}
              />
              {errors.towel_cost && (
                <p className="mt-1 text-sm text-danger-600">{errors.towel_cost}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Coste estimado de lavandería de toallas
              </p>
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="bed-disposition-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </div>
          ) : (
            isEditing ? 'Actualizar' : 'Crear'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BedDispositionDialog;
